import React, {useState} from 'react'
import styled from 'styled-components'
import { ChevronDown, EyeFill, PlusCircle, QuestionCircle } from '@styled-icons/bootstrap'
import { Edit, Settings, Plus } from '@styled-icons/feather'
import {Logout} from '@styled-icons/material-rounded/Logout'
import Link from 'next/link'
import {injectStore} from '../../../store/utils'
import Avatar from '../../primitives/Avatar/Avatar'
import {PAGES_ROUTES} from '../../../configs/routes'
import {theme} from '../../styles/theme'
import Portal from '../Portal/Portal'
import {rem} from 'polished'
import Input from "../../primitives/Input/Input"
import {StyledCover} from "../../primitives/ColorPicker/InputColorPicker"
import { useTranslation } from 'react-i18next'
import { isMobile } from '../../../utils/helpers'
import LangButtons from '../LangButtons/LangButtons'
import {SpellCheck} from '@styled-icons/fa-solid'
import {brand, helpLinkTarget, isAbMode} from '../../../brand'

const StyledDropdown = styled.div`
  display: inline-block;
  vertical-align: middle;
  position: relative;
`

const StyledName = styled.div`
  font-size: .8rem;
  border: 1px solid #D8D8D8;
  border-radius: 6px;
  padding: .35rem 2.22rem .35rem .55rem;
  line-height: 1;
  max-width: 210px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--brand-ink, #2D055B);
  cursor: pointer;
  position: relative;
  user-select: none;

  svg {
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;
    right: .8rem;
    color: #939393;
  }
`

const StyledContent = styled.div`
  position: fixed;
  z-index: 9999;
  padding: 1.4rem 0 0;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 .55rem 1.11rem 0 rgba(19, 10, 31, 0.07);
  width: ${rem(250)};
  margin-top: ${rem(10)};
`

const DropdownLink = styled.a`
  display: block;
  color: var(--brand-ink, #2D055B);
  transition: background .3s;
  padding: .83rem;
  cursor: pointer;

  svg {
    margin-top: -4px;
    margin-right: .83rem;
  }

  &:hover {
    color: var(--brand-ink, #2D055B);
    background: rgba(0, 0, 0, 0.02);
    text-decoration: none;
  }
`

const DropdownButton = styled.button`
  display: block;
  color: var(--brand-ink, #2D055B);
  transition: background .3s;
  padding: .83rem;
  background: none;
  border: 0;
  width: 100%;
  text-align: left;

  svg {
    margin-top: -4px;
    margin-right: .83rem;
  }

  &:hover {
    color: var(--brand-ink, #2D055B);
    background: rgba(0, 0, 0, 0.02);
    text-decoration: none;
  }
`

const UserName = styled.div`
  font-size: 1.11rem;
  text-align: center;
  margin: 1.7rem 0 .55rem;
`

const UserWebsite = styled.div`
  font-size: .8rem;
  color: #6c757d;
  font-weight: 300;
`

const SiteItem = styled.div`
  font-size: .8rem;
  line-height: 1.3;
  color: #6c757d;
  font-weight: 300;
  padding: .8rem .83rem;
  transition: background .3s;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`

const StyledWebsites = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 200px;
`

const TopbarDropdown = (
  {
    logout,
    fullName,
    selectedStore = {},
    userWebsites,
    switchStore,
    avatar,
    isShopify,
    isAdmin,
    widgets,
    progress,
    // urlToPricing,
    loadStores,
    activateExtensionPackage,
    isDialogueFeaturesLocked,
    setStoreQuery,
    hasMore,
  }: any) => {
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const [position, setPosition] = useState<React.CSSProperties>({
    left: 0,
    top: 0,
  })
  const { t } = useTranslation()

  const hideDropdown = () => {
    setDropdownVisible(false)
  }

  let widget
  const { valid_widgets } = progress

  if (widgets.length) {
    widget = widgets.reduce((a: any, b: any) => (new Date(a.created_at) > new Date(b.created_at) ? a : b))
  }

  const showDropdown = () => {
    if (dropdownRef.current) {
      loadStores(true)
      setStoreQuery('')
      setDropdownVisible(!dropdownVisible)
      const {bottom} = dropdownRef.current.getBoundingClientRect()
      setPosition({right: 20, top: bottom})
    }
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    if (scrollTop >= scrollHeight - clientHeight - 30 && hasMore) {
      loadStores()
    }
  }

  return (
    <StyledDropdown ref={dropdownRef}>
      <StyledName onClick={showDropdown}>
        <Avatar className='mr-2' src={avatar}/>
        {selectedStore.name}
        <ChevronDown size={16}/>
      </StyledName>
      {dropdownVisible && <Portal id={'modals'}>
        <StyledCover onClick={hideDropdown} style={{zIndex: 9}}/>
        <StyledContent style={position}>
          <Avatar className='m-auto d-block'
                  borderWidth={3}
                  size={70}
                  src={avatar} alt='avatar'/>
          <div className='text-center'>
            <UserName>{fullName}</UserName>
            <UserWebsite title={selectedStore.id}>{selectedStore.name}</UserWebsite>
          </div>
          <hr className='mx-3'/>
          {isAdmin && <div className="px-3">
            <Input
              autoFocus
              onChange={(e: any) => setStoreQuery(e.target.value)}
              size="tiny"
              placeholder="Search by name, url, id"/>
          </div>}
          <StyledWebsites onScroll={handleScroll}>
            {userWebsites.map((item: any) => (
              <SiteItem key={item.id} onClick={() => switchStore(item.id)}>
                <div className='d-flex align-items-center'>
                  <Avatar className='mr-3'
                          borderWidth={2}
                          src={avatar} alt='avatar'/>
                  <div style={{minWidth: 0}}>
                    <p className={'text-truncate'}>{item.name}</p>
                    <p className={'text-truncate'}>{item.url}</p>
                  </div>
                </div>
              </SiteItem>
            ))}
          </StyledWebsites>
          {
            (isShopify || isDialogueFeaturesLocked || isAbMode) ? null : (
              <a className="no-tdu" href={PAGES_ROUTES.settings.addNewStore}>
                <SiteItem>
                  <div className='d-flex align-items-center'>
                    <div className={'mr-3'}><PlusCircle size={26} color={theme.colors.text}/></div>
                    <div style={{color: theme.colors.text}}>{t('header.add_new_store')}</div>
                  </div>
                </SiteItem>
              </a>
            )
          }
          <hr className='mx-3'/>
          {
            isMobile &&  <DropdownLink><LangButtons/></DropdownLink>
          }
          {
            isMobile && valid_widgets && <>
              <Link href={selectedStore?.url} passHref>
                <DropdownLink><EyeFill strokeWidth={2} size={15}/>{t('header.tip_my_store')}</DropdownLink>
              </Link>
              {
                widget && <Link href={PAGES_ROUTES.widgets.edit(widget.content_type, widget.id, widget.preset_type)} passHref>
                  <DropdownLink><Edit strokeWidth={2} size={15}/>{t('editor')}</DropdownLink>
                </Link>
              }
            </>
          }
          <DropdownLink href={PAGES_ROUTES.settings.account}><Settings size={15} strokeWidth={2}/>{t('settings')}</DropdownLink>
          {/*{(!isAdmin && !isDialogueFeaturesLocked) && <Link href={urlToPricing} passHref>*/}
          {/*  <DropdownLink><BookOutline size={16}/>{t('billing')}</DropdownLink>*/}
          {/*</Link>}*/}
          {isAdmin && !isAbMode && <DropdownLink href={PAGES_ROUTES.settings.createStoreAdmin}><Plus size={15} strokeWidth={2}/>Create store</DropdownLink>}
          {isAdmin && !isAbMode && <DropdownLink onClick={activateExtensionPackage}>
            <SpellCheck strokeWidth={2} size={16}/>Activate AB-Commerce
          </DropdownLink>}
          <DropdownButton
            as="a" href={brand.helpUrl}
            rel="noreferrer" target={helpLinkTarget(brand.helpUrl)}>
            <QuestionCircle size={16}/>{isAbMode ? 'Contact support' : t('help_center')}</DropdownButton>
          <DropdownButton onClick={logout}><Logout size={16}/>{t('log_out')}</DropdownButton>
        </StyledContent>
      </Portal>
      }
    </StyledDropdown>
  )
}


export default injectStore(({userStore, widgetStore}) => {
  return {
    logout: userStore.logout,
    selectedStore: userStore.selectedStore,
    userWebsites: userStore.userWebsites,
    switchStore: userStore.switchStore,
    fullName: userStore.fullName,
    avatar: userStore.avatarUrl,
    isShopify: userStore.isShopifyUser,
    isAdmin: userStore.isAdmin,
    widgets: widgetStore.widgets,
    progress: userStore.progressIndicator,
    urlToPricing: userStore.urlToPricing,
    loadStores: userStore.loadStores,
    activateExtensionPackage: userStore.activateExtensionPackage,
    isDialogueFeaturesLocked: userStore.isDialogueFeaturesLocked,
    setStoreQuery: userStore.setStoreQuery,
    hasMore: userStore.stores.hasMore,
  }
})(TopbarDropdown)
